import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import * as Yup from "yup";
import { useDebouncedApiEffect } from "../../../hooks/useDebouncedApiEffect";
import SkeletonTicketList from "../../../loaders/SkeletonTicketList";
import { tokens } from "../../../public/tokens";
import {
  DeleteCampusBuilding,
  GetCampusBuildingsList,
  PostSaveCampusBuilding,
  PutSaveCampusBuilding,
  PutSaveCampusDetail,
} from "../../../services/CampusBuildingService";
import { GetNetsuiteCampusListCancelToken } from "../../../services/ProgrammeServices";
import {
  getOperatingHoursRows,
  mapBuildingOrRoomsForTable,
} from "../../../utils/CampusBuildingsUtil";
import Button from "../../common/Button/Button";
import DataTableComponent from "../../common/DataTableComponent";
import FormInput from "../../common/forms/FormInput";
import FormLabel from "../../common/forms/FormLabel";
import AddEditBuildingModal from "./AddEditBuildingModal";
import { createLinkButtonCell, getTableHeader } from "./TableComponents";

const { colors, typography, effects, globals } = tokens;
const { neutral, states, text: textColors, ui, surfaces } = colors;

const customStyles = {
  tableWrapper: {
    style: { marginTop: 0, paddingTop: 0 },
  },
  table: {
    style: { marginTop: 0, paddingTop: 0, borderTop: "none" },
  },
  head: {
    style: { borderTop: "none", marginTop: 0, paddingTop: 0 },
  },
  headRow: {
    style: {
      backgroundColor: neutral.white,
      borderBottom: `1px solid ${ui.border}`,
      minHeight: "48px",
      marginTop: 0,
      paddingTop: 0,
    },
  },
  headCells: {
    style: { paddingLeft: "10px", paddingRight: "10px" },
  },
  rows: {
    style: {
      backgroundColor: neutral.white,
      "&:nth-child(even)": { backgroundColor: surfaces["page-background"] },
    },
  },
  cells: {
    style: {
      paddingLeft: "10px",
      paddingRight: "10px",
      fontFamily: globals["font-family"],
    },
  },
  pagination: {
    style: {
      borderTop: "none",
      paddingTop: "16px",
      paddingBottom: "16px",
      fontFamily: globals["font-family"],
    },
    pageButtonsStyle: {
      borderRadius: "6px",
      height: "32px",
      width: "32px",
      cursor: "pointer",
      transition: "0.2s",
      margin: "0 2px",
    },
  },
};

const cardStylePadding = "21px";

const cardStyle = {
  borderRadius: globals["border-radius"],
  border: `1px solid ${ui.border}`,
  boxShadow: effects.effects.shadow.default,
  backgroundColor: neutral.white,
  padding: cardStylePadding
};

const subHeaderStyle = {
  fontSize: typography.fontSize["xl-18px"],
  fontWeight: typography.fontWeights.semibold,
  color: textColors.primary,
  fontFamily: globals["font-family"],
  marginBottom: '10px'
};

const renderHtmlCell = (value) => ({ __html: value || "" });

const renderOperatingHoursCell = (row) => {
  const rows = getOperatingHoursRows(row.operatingHoursObj);
  if (!rows.length) return <span>-</span>;
  return (
    <div style={{ lineHeight: "1.4", fontSize: typography.fontSize["lg-14px"] }}>
      {rows.map((item, idx) => (
        <div key={idx} style={{ display: "flex", gap: "8px" }}>
          <span style={{ width: "60px", color: textColors.secondary }}>{item.label}</span>
          <span>{item.time}</span>
        </div>
      ))}
    </div>
  );
};

const CampusDetails = ({
  campusId,
  campusName,
  campusDetailsData,
  setCampusDetailsData,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingFirstTime, setIsLoadingFirstTime] = useState(true);
  const [netsuiteBuildingList, setNetsuiteBuildingList] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [saveLoader, setSaveLoader] = useState(false);
  const [originalValues, setOriginalValues] = useState({});

  const [total, setTotal] = useState(0);
  const [tableState, setTableState] = useState({
    page: 1,
    perPage: 10,
    sortKey: "name",
    sortOrder: "asc",
  });
  const [buildingSearch, setBuildingSearch] = useState("");

  const [showBuildingModal, setShowBuildingModal] = useState(false);
  const [editingBuilding, setEditingBuilding] = useState(null);
  const [savingBuilding, setSavingBuilding] = useState(false);
  
  const fetchBuildings = useCallback(async (args) => {
    const cancelToken = args?.cancelToken;
    if (!campusId) {
      return;
    }
    setIsLoading(true);
    try {
      const res = await GetCampusBuildingsList(campusId, {
        start: (tableState.page - 1) * tableState.perPage,
        count: tableState.perPage,
        sort: tableState.sortKey,
        order: tableState.sortOrder,
        search: buildingSearch
      }, cancelToken);

      if (res.data?.campusBuildingDetails) {
        const mapped = res.data.campusBuildingDetails.map(mapBuildingOrRoomsForTable);
        setBuildings(mapped);
        if (res.data?.range) {
          setTotal(res.data.range.total);
        }
      } else {
        setBuildings([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("Error fetching buildings:", error);
      Swal.fire("Error!", "Failed to load buildings.", "error");
    } finally {
      setIsLoading(false);
      setIsLoadingFirstTime(false);
    }
  }, [campusId, tableState, buildingSearch]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    const fetchNetsuiteData = async () => {
      try {
        const res = await GetNetsuiteCampusListCancelToken("", source.token);
        if (res.data?.data?.items?.length) {
          setNetsuiteBuildingList(res.data.data.items);
        } else {
          Swal.fire("Error!", "Failed to load campus data. Reload page or try again later.", "error");
        }
      } catch (error) {
        if (!axios.isCancel(error)) console.error(error);
      }
    };
    fetchNetsuiteData();
    return () => source.cancel("Component unmounted");
  }, []);
  
  // Purpose: When search or sort changes, it must refetch
  useDebouncedApiEffect(fetchBuildings, [campusId, tableState, buildingSearch]);

  useEffect(() => {
    console.log('useEffectcampusDetailsData');
    if (campusDetailsData) {
      setOriginalValues({
        name: campusDetailsData.campus_name || "",
        marketing_name: campusDetailsData.marketing_name || "",
        marketing_name_short: campusDetailsData.marketing_name_short || "",
        url: campusDetailsData.url || "",
      });
    }
  }, [campusDetailsData]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: campusDetailsData?.campus_name || "",
      marketing_name: campusDetailsData?.marketing_name || "",
      marketing_name_short: campusDetailsData?.marketing_name_short || "",
      url: campusDetailsData?.url || "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required").max(255),
      marketing_name: Yup.string().required("Marketing Name is required").max(500),
      marketing_name_short: Yup.string().required("Marketing Short Name is required").max(100),
      url: Yup.string().required("URL is required").max(255),
    }),
    onSubmit: async (values) => {
      setSaveLoader(true);
      try {
        const res = await PutSaveCampusDetail(campusId, values);
        const updated = res.data?.updatedCampusDetail;
        if (updated) {
          setOriginalValues({ ...updated });
          setCampusDetailsData((prev) => ({
            ...prev,
            campus_name: updated.name,
            marketing_name: updated.marketing_name,
            marketing_name_short: updated.marketing_name_short,
            url: updated.url,
          }));
        }
        await Swal.fire("Success!", "Campus details have been saved.", "success");
      } catch (error) {
        Swal.fire("Error", error.message || "Failed to save campus details.", "error");
      } finally {
        setSaveLoader(false);
      }
    },
  });

  const handleAddBuilding = () => {
    setEditingBuilding(null);
    setShowBuildingModal(true);
  };

  const handleEditBuilding = (building) => {
    setEditingBuilding(building);
    setShowBuildingModal(true);
  };

  const handleSaveBuilding = async (buildingData) => {
    setSavingBuilding(true);
    try {
      if (editingBuilding) {
        await PutSaveCampusBuilding(buildingData, campusId);
      } else {
        await PostSaveCampusBuilding(buildingData, campusId);
      }
      await fetchBuildings();
      Swal.fire("Success!", `Building ${editingBuilding ? "updated" : "added"} successfully.`, "success");
    } catch (error) {
      console.error(error);
      Swal.fire("Error!", "Failed to save building.", "error");
    } finally {
      setSavingBuilding(false);
      setShowBuildingModal(false);
      setEditingBuilding(null);
    }
  };

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Delete "${row.name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: states.alert.default,
      cancelButtonColor: "var(--button-color)",
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const res = await DeleteCampusBuilding(row.id);
          return !!res.data?.deletedBuilding?.id;
        } catch (error) {
          Swal.showValidationMessage(`Request failed: ${error}`);
          return false;
        }
      },
    });
    if (result.isConfirmed) {
      await fetchBuildings();
      Swal.fire("Deleted!", "Building deleted.", "success");
    }
  };

  const columns = [
    {
      name: getTableHeader('Name', tableState.sortKey === "name"),
      selector: 'name',
      sortField: "name",
      sortable: true,
      grow: 1.2,
      cell: createLinkButtonCell({ onClick: handleEditBuilding, getLabel: (row) => row.name, getTitle: (row) => row.name })
    },
    {
      name: getTableHeader('Grouping Colour'),
      selector: 'grouping_color',
      sortField: "grouping_color",
      sortable: false,
      minWidth: "150px",
      cell: (row) => (
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <span
            style={{
              display: "inline-block",
              width: "12px",
              height: "12px",
              borderRadius: "2px",
              border: `1px solid ${ui.border}`,
              backgroundColor: row.grouping_color || ui.primary,
            }}
          />
          <span>{row.grouping_color}</span>
        </div>
      ),
    },
    {
      name: getTableHeader('Operating Hours'),
      sortable: false,
      minWidth: "190px",
      grow: 1.4,
      cell: renderOperatingHoursCell,
    },
    {
      name: getTableHeader('Address'),
      selector: 'address',
      sortable: false,
      grow: 1.8,
      cell: (row) => (
        <div
          dangerouslySetInnerHTML={renderHtmlCell(row.address)}
          title={row.address?.replace(/<[^>]*>/g, "") || "-"}
        />
      ),
    },
    {
      name: getTableHeader('Actions'),
      sortable: false,
      width: "120px",
      cell: (row) => (
        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-start" }}>
          <Button
            className="btn btn-primary campus-action-button campus-action-button--edit"
            variant="primary"
            iconOnly
            iconLeft={
              <i
                className="fal fa-edit"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: 0,
                  lineHeight: 1,
                }}
              />
            }
            onClick={() => handleEditBuilding(row)}
            title="Edit"
          />

          <Button
            className="btn btn-danger campus-action-button campus-action-button--delete"
            variant="danger"
            iconOnly
            iconLeft={
              <i
                className="fal fa-trash-alt"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: 0,
                  lineHeight: 1,
                }}
              />
            }
            onClick={() => handleDelete(row)}
            title="Delete"
          />
        </div>
      ),
    },
  ];

  if (isLoadingFirstTime) {
    return <SkeletonTicketList />;
  }

  return (
    <>
      <div className="card-profile-info-card" style={{ border: "none", background: "transparent" }}>
        <div className="card-body" style={{ background: 'transparent', padding: 0 }}>
          <div
            className="card mb-4"
            style={cardStyle}
          >
            <div className="card-body" style={{ padding: globals["spacing-lg"] }}>
              <form onSubmit={formik.handleSubmit}>
                <div className="row" style={{ gap: "1.75rem", marginBottom: "1.5rem" }}>
                  <div className="col-md-3" style={{ flex: 1 }}>
                    <div className="form-group mb-0">
                      <FormLabel required>Name</FormLabel>
                      <FormInput
                        {...formik.getFieldProps("name")}
                        error={formik.errors.name}
                        touched={formik.touched.name}
                        placeholder="Name"
                      />
                    </div>
                  </div>
                  <div className="col-md-3" style={{ flex: 1 }}>
                    <div className="form-group mb-0">
                      <FormLabel required>Marketing Name</FormLabel>
                      <FormInput
                        {...formik.getFieldProps("marketing_name")}
                        error={formik.errors.marketing_name}
                        touched={formik.touched.marketing_name}
                        placeholder="Marketing Name"
                      />
                    </div>
                  </div>
                  <div className="col-md-3" style={{ flex: 1 }}>
                    <div className="form-group mb-0">
                      <FormLabel required>Marketing Short Name</FormLabel>
                      <FormInput
                        {...formik.getFieldProps("marketing_name_short")}
                        error={formik.errors.marketing_name_short}
                        touched={formik.touched.marketing_name_short}
                        placeholder="Marketing Short Name"
                      />
                    </div>
                  </div>
                  <div className="col-md-3" style={{ flex: 1 }}>
                    <div className="form-group mb-0">
                      <FormLabel required>URL</FormLabel>
                      <FormInput
                        {...formik.getFieldProps("url")}
                        error={formik.errors.url}
                        touched={formik.touched.url}
                        placeholder="URL"
                      />
                    </div>
                  </div>
                </div>
                <div className="form-group mb-0" style={{ display: "flex", gap: "12px" }}>
                  <Button
                    type="submit"
                    variant="primary"
                    iconLeft={<i className="fal fa-save" />}
                    isLoading={saveLoader}
                    disabled={!formik.dirty || !formik.isValid || saveLoader}
                  >
                    {saveLoader ? "Saving..." : "Save"}
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    iconLeft={<i className="fal fa-times" />}
                    onClick={() => formik.resetForm({ values: originalValues })}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>

          <div className="mt-4">
            <h4 style={subHeaderStyle}>
              Buildings
            </h4>
            <DataTableComponent
              data={buildings}
              columns={columns}
              state={tableState}
              setState={setTableState}
              setSearch={setBuildingSearch}
              totalRows={total}
              loading={isLoading}
              isExport={false}
              btnStyleOverride={{ borderRadius: globals["border-radius"] }}
              containerStyle={{
                borderRadius: globals["border-radius"],
                padding: cardStylePadding
              }}
              otherTableProps={{
                highlightOnHover: true,
                customStyles,
                responsive: true
              }}
              tableButton={[
                <Button
                  className="btn btn-primary"
                  variant="primary"
                  iconLeft={<i className="fal fa-plus" />}
                  onClick={handleAddBuilding}
                  style={{ borderRadius: globals["border-radius"] }}
                >
                  Add Buildings
                </Button>
              ]}
            />
          </div>
        </div>
      </div>

      <AddEditBuildingModal
        show={showBuildingModal}
        onHide={() => {
          setShowBuildingModal(false);
          setEditingBuilding(null);
        }}
        building={editingBuilding}
        campusId={campusId}
        onSave={handleSaveBuilding}
        saving={savingBuilding}
        netsuiteBuildingList={netsuiteBuildingList}
      />
    </>
  );
};

export default CampusDetails;
